// The award review (Lane 1395; mockup Ryan accepted 2026-10-07): who gets what, what still
// blocks the award, the exact notice each supplier gets, then one click. Every figure and
// sentence comes from the server's review read, which the award itself checks again under
// the event lock; a change since the review is refused and this screen reloads.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StatTile from './StatTile';
import { formatAmount, formatUnitPrice, formatUnits, newKey } from './sourcingApi';
import './negotiation.css';

const GROUP_LABEL = { award_won: 'Winners', award_kept: 'Keep today’s parts', award_not_won: 'Not awarded' };

// The first three tiles compare the same lines: when some lines have no price today, all
// three cover the lines that do, and say so (as the Overview's change tile does). Saved is
// green; more is red in brackets.
export function AwardTiles({ totals: t, currency, fourth, awardLabel = 'With this award' }) {
  const partial = t.lines_without_today > 0;
  const known = t.lines - t.lines_without_today;
  const on = partial ? `On ${known} of ${t.lines} lines` : null;
  const v = Number(t.change_known);
  const amount = formatAmount(Math.abs(v), currency, 0);
  return (
    <div className="srce-tiles">
      <StatTile label="Today" value={formatAmount(t.today_known, currency, 0)}
        note={on || "Last 12 months' prices"} />
      <StatTile label={awardLabel} value={formatAmount(partial ? t.projected_known : t.projected, currency, 0)}
        note={on || 'At the quoted prices'} />
      <StatTile label="Change against today" value={v >= 0 ? amount : `(${amount})`}
        tone={v > 0 ? 'good' : v < 0 ? 'crit' : null} note={on || (v >= 0 ? 'Saved' : 'More than today')} />
      {fourth}
    </div>
  );
}

function Checks({ checks }) {
  const shown = checks.filter((c) => c.level !== 'ok');
  return (
    <section className="srce-card2" data-testid="award-checks">
      <div className="srce-card2-h"><span className="srce-card2-t">Before you award</span></div>
      <div className="srce-card2-f srce-stack">
        {shown.length === 0 && <div className="srce-good">Everything is ready.</div>}
        {shown.map((c) => (
          <div key={c.key} className={`srce-award-check srce-award-check--${c.level}`}>
            <span className="srce-award-dot" aria-hidden="true">{c.level === 'block' ? '×' : '!'}</span>
            <span><span className="srce-sr">{c.level === 'block' ? 'Blocks the award: ' : 'Note: '}</span>{c.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Shares({ title, rows, currency, kept }) {
  if (!rows.length) return null;
  return (
    <section className="srce-card2">
      <div className="srce-card2-h"><span className="srce-card2-t">{title}</span></div>
      <div className="srce-scroll">
        <table className="srce-sc-tbl">
          <thead>
            <tr><th>Part</th><th>Description</th><th>Site</th><th>{kept ? 'Stays with' : 'Supplier'}</th>
              <th className="srce-num">Quantity</th><th className="srce-num">{kept ? 'Today’s price' : 'Price'}</th>
              <th className="srce-num">{kept ? 'Today’s cost' : 'Cost'}</th>{!kept && <th>Note</th>}</tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={`${x.line_uid}-${x.supplier_id}`}>
                <td>{x.part}</td><td title={x.description || ''}>{x.description || ''}</td><td>{x.site || ''}</td>
                <td title={x.supplier_name || ''}>{x.supplier_name || 'Today’s supplier'}</td>
                <td className="srce-num">{formatUnits(x.units)}</td>
                <td className="srce-num">{x.price === null ? 'None' : formatUnitPrice(x.price, currency)}</td>
                <td className="srce-num">{x.cost === null ? 'None' : formatAmount(x.cost, currency)}</td>
                {!kept && <td className="srce-warn-text">{x.qualification_pending ? 'Subject to qualification' : ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Notices({ notices }) {
  const groups = notices.groups.filter((g) => g.suppliers.length > 0);
  const [pick, setPick] = useState(groups.length ? groups[0].kind : null);
  const g = groups.find((x) => x.kind === pick) || groups[0];
  return (
    <section className="srce-card2" data-testid="award-notices">
      <div className="srce-card2-h"><span className="srce-card2-t">What suppliers are told</span></div>
      <div className="srce-card2-f srce-stack">
        {groups.length === 0 && <div className="srce-mut">No supplier has joined the event, so no one is told.</div>}
        {groups.length > 0 && (
          <div className="srce-row" role="group" aria-label="Notice">
            {groups.map((x) => (
              <button key={x.kind} type="button" aria-pressed={x.kind === g.kind}
                className={`neg-chip${x.kind === g.kind ? ' neg-chip--on' : ''}`} onClick={() => setPick(x.kind)}>
                {GROUP_LABEL[x.kind]} ({x.suppliers.length})</button>
            ))}
          </div>
        )}
        {g && (
          <div className="srce-award-notice">
            <div className="srce-mut">To: {g.suppliers.map((s) => s.supplier_name).join(', ')}</div>
            <div className="srce-strong">{g.title}</div>
            <div>{g.body}</div>
          </div>
        )}
        {notices.nobody.length > 0 && <ToldNothing nobody={notices.nobody} />}
      </div>
    </section>
  );
}

const OUTCOME_TEXT = { award_won: 'Wins parts', award_kept: 'Keeps today’s parts', award_not_won: 'Not awarded' };
const plural = (n, one, many) => (n === 1 ? one : many);

// The suppliers told nothing (QA 2026-10-07: was a wall of names): one line with the
// count and why, the winners no one can tell named in it, the list behind "Show who".
function ToldNothing({ nobody }) {
  const [open, setOpen] = useState(false);
  const joined = nobody.filter((s) => s.why === 'not_joined');
  const wins = joined.filter((s) => s.outcome === 'award_won').length;
  const removed = nobody.filter((s) => s.why === 'removed').length;
  const declined = nobody.filter((s) => s.why === 'declined').length;
  const why = [];
  if (joined.length) {
    why.push(`${joined.length} ${plural(joined.length, 'has', 'have')} no one in the supplier portal${
      wins ? `, including ${wins} that ${plural(wins, 'wins', 'win')} parts` : ''}.`);
  }
  if (removed) why.push(`${removed} ${plural(removed, 'was', 'were')} removed from the event.`);
  if (declined) why.push(`${declined} declined and won nothing.`);
  return (
    <div className="srce-stack" data-testid="award-told-nothing">
      <div className="srce-award-nothing">
        <span><span className="srce-strong">{nobody.length} {plural(nobody.length, 'supplier is', 'suppliers are')} told nothing.</span> {why.join(' ')}</span>
        <button type="button" className="srce-btn" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show who'}</button>
      </div>
      {open && (
        <div className="srce-scroll">
          <table className="srce-sc-tbl">
            <thead><tr><th>Supplier</th><th>In this award</th><th>Why told nothing</th></tr></thead>
            <tbody>
              {nobody.map((s) => (
                <tr key={s.supplier_id}><td>{s.supplier_name}</td><td>{OUTCOME_TEXT[s.outcome] || '-'}</td><td>{s.reason}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// The award sends the event revision the review was read at (Codex award r1 #3), so a
// reload after a refusal also refreshes the revision it sends.
export default function AwardReview({ api, eventId, resultId, onAwarded, onCancel }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  // One request key per reviewed answer: a click whose reply was lost replays, never
  // awards twice.
  const key = useRef(newKey());
  const load = useCallback(() => {
    setError('');
    return api.awardReview(eventId, resultId).then((d) => { setData(d); key.current = newKey(); })
      .catch((e) => setError(e.message));
  }, [api, eventId, resultId]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading the award review…</div>;
  const currency = data.currency || 'USD';
  if (data.state === 'awarded') return <div className="srce-mut">This event is already awarded. See the Award tab.</div>;
  if (data.state === 'not_live') return <div className="srce-mut">Only a published event can be awarded.</div>;
  const blocked = data.state !== 'ready';
  const t = data.totals;
  const winners = t ? new Set((data.award || []).map((x) => x.supplier_id)).size : 0;
  const award = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.award(eventId, data.revision, { result_id: data.result_id, input_digest: data.input_digest,
        rationale: why.trim() || null }, key.current);
      onAwarded();
    } catch (e) {
      if (e.code === 'award_out_of_date' || e.code === 'stale_revision') {
        setNotice('Something changed since you opened this review (for example a new offer). Here is the review again.');
        await load();
      } else if (e.code === 'award_blocked') {
        // Codex award r1 #4: the checks changed (for example a condition reopened); the
        // reloaded review names what blocks the award now.
        setNotice('The award cannot be made yet. What blocks it is listed below.');
        await load();
      } else {
        setError(e.message);
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="srce-ov srce-award" data-testid="award-review">
      <h2 className="srce-award-h">Award {data.scenario.number ? `#${data.scenario.number} ` : ''}{data.scenario.name}</h2>
      {notice && <div className="srce-notice" role="status">{notice}</div>}
      {t && (
        <AwardTiles totals={t} currency={currency} fourth={
          <StatTile label="Winners" value={`${winners} ${winners === 1 ? 'supplier' : 'suppliers'}`}
            note={`${t.award_lines} ${t.award_lines === 1 ? 'line' : 'lines'} awarded`} />} />
      )}
      <Checks checks={data.checks || []} />
      {data.award && <Shares title={`Who gets what (${data.award.length} ${data.award.length === 1 ? 'line' : 'lines'})`} rows={data.award} currency={currency} />}
      {data.no_change && <Shares title={`No change (${data.no_change.length} ${data.no_change.length === 1 ? 'line stays' : 'lines stay'} with today’s supplier)`} rows={data.no_change} currency={currency} kept />}
      {data.notices && (
        <section className="srce-card2">
          <div className="srce-card2-f srce-stack">
            <label htmlFor="award-why" className="srce-strong">Why this award (optional, your team only)</label>
            <textarea id="award-why" className="srce-input" rows={2} maxLength={2000} value={why}
              onChange={(e) => setWhy(e.target.value)} />
          </div>
        </section>
      )}
      {data.notices && <Notices notices={data.notices} />}
      {error && <div className="srce-error" role="alert">{error}</div>}
      <div className="srce-row srce-award-foot">
        <span className="srce-mut">The award is final. The event becomes read only and the notices go out.</span>
        <span className="srce-push srce-row">
          <button type="button" className="srce-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="srce-btn srce-btn--primary" disabled={blocked || busy} onClick={award}>
            {busy ? 'Awarding…' : data.round_open ? `Close round ${data.round_open} and award` : 'Award'}</button>
        </span>
      </div>
    </div>
  );
}
